/**
 * Partner Portal Layout + PartnerPortalAuthGuard
 *
 * Provides the sidebar navigation shell for all authenticated partner-portal pages.
 * Uses a separate nav from the district/vendor/staff portals.
 * PartnerPortalAuthGuard redirects to /partner-portal/login if not authenticated.
 */

import React, { useEffect, ReactNode } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { usePartnerPortalAuthStore } from '../../../../shared/stores/partnerPortalAuthStore';

// ─────────────────────────────────────────────────────────────
// NAV CONFIG
// ─────────────────────────────────────────────────────────────

const PARTNER_PORTAL_NAV = [
  { path: '/partner-portal',              label: 'Dashboard',        icon: '⬡' },
  { path: '/partner-portal/how-it-works', label: 'How It Works',     icon: '?' },
  { path: '/partner-portal/guide',         label: 'Operations Guide',  icon: '📖' },
  { path: '/partner-portal/programs',     label: 'Programs',         icon: '⊞' },
  { path: '/partner-portal/referrals',    label: 'Referrals',        icon: '↘' },
  { path: '/partner-portal/applications', label: 'Applications',     icon: '📋' },
  { path: '/partner-portal/enrollments',  label: 'Roster',           icon: '≡' },
  { path: '/partner-portal/attendance',   label: 'Attendance Log',   icon: '✓' },
  { path: '/partner-portal/analytics',    label: 'Analytics',        icon: '◈' },
  { path: '/partner-portal/billing',      label: 'Funding & Billing',icon: '$' },
  { path: '/partner-portal/credentials',  label: 'Credentials',      icon: '🔐' },
  { path: '/partner-portal/team',         label: 'Team',             icon: '○' },
  { path: '/partner-portal/settings',     label: 'Settings',         icon: '⚙' },
];

// ─────────────────────────────────────────────────────────────
// AUTH GUARD
// ─────────────────────────────────────────────────────────────

export function PartnerPortalAuthGuard({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { token, canAccessPortal, user } = usePartnerPortalAuthStore();

  useEffect(() => {
    if (!token) {
      navigate('/partner-portal/login', { replace: true });
      return;
    }
    // Has token but 2FA not set up → enforce setup.
    // Superuser impersonation sessions bypass this; they don't own the account.
    if (user && !user.two_factor_enabled && !user.is_superuser_session) {
      navigate('/partner-portal/setup-2fa', { replace: true });
      return;
    }
    if (!canAccessPortal()) {
      navigate('/partner-portal/login', { replace: true });
    }
  }, [token, user, canAccessPortal, navigate]);

  if (!token || !canAccessPortal()) return null;
  return <>{children}</>;
}

// ─────────────────────────────────────────────────────────────
// LAYOUT
// ─────────────────────────────────────────────────────────────

interface PartnerPortalLayoutProps {
  children: ReactNode;
  title?: string;
}

export default function PartnerPortalLayout({ children, title }: PartnerPortalLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = usePartnerPortalAuthStore();

  const handleLogout = () => {
    logout();
    navigate('/partner-portal/login');
  };

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      {/* Sidebar */}
      <aside className="w-56 bg-slate-900 flex flex-col shrink-0">
        {/* Logo */}
        <div className="px-5 py-5 border-b border-slate-700">
          <p className="text-violet-400 font-bold text-sm">AgenticED</p>
          <p className="text-slate-400 text-xs mt-0.5">Partner Portal</p>
        </div>

        {/* Org name */}
        {user?.org && (
          <div className="px-5 py-3 border-b border-slate-700">
            <p className="text-white text-xs font-semibold truncate">{user.org.org_name}</p>
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium mt-1 ${
              user.org.verification_status === 'verified'
                ? 'bg-emerald-900/50 text-emerald-400'
                : 'bg-amber-900/50 text-amber-400'
            }`}>
              {user.org.verification_status === 'verified' ? 'Verified' : 'Pending Review'}
            </span>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 px-3 py-3 overflow-y-auto">
          {PARTNER_PORTAL_NAV.map(item => {
            const isActive = item.path === '/partner-portal'
              ? location.pathname === '/partner-portal'
              : location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm mb-0.5 transition-colors ${
                  isActive
                    ? 'bg-violet-700 text-white font-medium'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
              >
                <span className="text-xs w-4 text-center">{item.icon}</span>
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* User */}
        <div className="px-5 py-4 border-t border-slate-700">
          <p className="text-xs text-slate-400 truncate">{user?.email}</p>
          <div className="flex items-center gap-3 mt-1">
            <button
              onClick={handleLogout}
              className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
            >
              Sign out
            </button>
            <button
              onClick={() => { logout(); window.location.assign('/admin/fsf'); }}
              className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
              title="Return to the demo persona picker"
            >
              ← Exit to Login
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        {title && (
          <div className="bg-white border-b border-slate-200 px-6 py-4">
            <h1 className="text-lg font-semibold text-slate-900">{title}</h1>
          </div>
        )}
        {children}
      </main>
    </div>
  );
}
